import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  clampDisplayIndex,
  clampLoopInternal,
  displayIndexFromInternal,
  initialInternalIndex,
} from '../utils/photoCarouselLoop'
import { readCarouselTrackTranslateX } from '../utils/readCarouselTrackTranslateX'
import ListingNoPhotoPlaceholder from './ListingNoPhotoPlaceholder'
import ListingPhotoBrandOverlay from './ListingPhotoBrandOverlay'

type Props = {
  urls: string[]
  isUserPhoto: boolean
  onTap?: () => void
  onPrefetch?: () => void
}

const IOS_GALLERY_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)'
const SNAP_RATIO = 0.2
const SNAP_MIN_PX = 44
const TAP_MOVE_MAX_PX = 8
const AXIS_LOCK_PX = 8
const WHEEL_STEP_PX = 48

type DragStart = {
  x: number
  y: number
  pointerId: number
  axis: 'x' | 'y' | null
}

export default function ListingCardPhotoCarousel({ urls, isUserPhoto, onTap, onPrefetch }: Props) {
  const { t } = useTranslation()
  const viewportRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const [viewportWidth, setViewportWidth] = useState(0)
  const [activePhoto, setActivePhoto] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const dragStartRef = useRef<DragStart | null>(null)
  const dragPxRef = useRef(0)
  const lastDragPxRef = useRef(0)
  const movedRef = useRef(false)
  const internalSlideRef = useRef(initialInternalIndex(urls.length))
  const viewportWidthRef = useRef(0)
  const rafRef = useRef<number | null>(null)
  const suppressClickRef = useRef(false)
  const wheelAccumRef = useRef(0)
  const animatingRef = useRef(false)
  const animateRafRef = useRef<number | null>(null)
  viewportWidthRef.current = viewportWidth

  const photosKey = useMemo(() => urls.join('\u0001'), [urls])
  const prevPhotosKeyRef = useRef(photosKey)
  const loop = urls.length > 1

  const slideUrls = useMemo(() => {
    if (!loop) return urls
    return [urls[urls.length - 1], ...urls, urls[0]]
  }, [photosKey, loop, urls])

  useEffect(() => {
    const el = viewportRef.current
    if (!el || urls.length <= 1) return
    const onTouchMove = (ev: TouchEvent) => {
      if (dragStartRef.current?.axis === 'x') ev.preventDefault()
    }
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    return () => el.removeEventListener('touchmove', onTouchMove)
  }, [urls.length])

  useEffect(() => {
    const el = viewportRef.current
    if (!el) return
    const measure = () => {
      const w = el.clientWidth
      if (w > 0 && Math.abs(w - viewportWidthRef.current) > 0.5) {
        viewportWidthRef.current = w
        setViewportWidth(w)
      }
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const paintTrack = useCallback((animate: boolean) => {
    const track = trackRef.current
    const w = viewportWidthRef.current
    if (!track || w <= 0) return
    const x = -internalSlideRef.current * w + dragPxRef.current
    if (animate) animatingRef.current = true
    else animatingRef.current = false
    track.style.transition = animate ? `transform 0.38s ${IOS_GALLERY_EASE}` : 'none'
    track.style.transform = `translate3d(${x}px, 0, 0)`
  }, [])

  const animateTrackTo = useCallback((targetInternal: number) => {
    const track = trackRef.current
    const w = viewportWidthRef.current
    if (!track || w <= 0) return
    const targetX = -targetInternal * w
    if (animateRafRef.current != null) {
      cancelAnimationFrame(animateRafRef.current)
      animateRafRef.current = null
    }
    animatingRef.current = true
    const fallbackFromX = -internalSlideRef.current * w + dragPxRef.current
    const fromX = readCarouselTrackTranslateX(track, fallbackFromX)
    track.style.transition = 'none'
    track.style.transform = `translate3d(${fromX}px, 0, 0)`
    void track.getBoundingClientRect()
    animateRafRef.current = requestAnimationFrame(() => {
      animateRafRef.current = null
      track.style.transition = `transform 0.38s ${IOS_GALLERY_EASE}`
      track.style.transform = `translate3d(${targetX}px, 0, 0)`
    })
  }, [])

  const snapCloneIfNeeded = useCallback(() => {
    if (dragStartRef.current) return
    const n = urls.length
    if (n <= 1) return
    const internal = internalSlideRef.current
    if (internal === 0) {
      internalSlideRef.current = n
      setActivePhoto(n - 1)
      paintTrack(false)
    } else if (internal === n + 1) {
      internalSlideRef.current = 1
      setActivePhoto(0)
      paintTrack(false)
    }
  }, [urls.length, paintTrack])

  useEffect(() => {
    const track = trackRef.current
    if (!track || !loop) return
    const onTransitionEnd = (ev: TransitionEvent) => {
      if (ev.propertyName !== 'transform') return
      animatingRef.current = false
      snapCloneIfNeeded()
    }
    track.addEventListener('transitionend', onTransitionEnd)
    return () => track.removeEventListener('transitionend', onTransitionEnd)
  }, [loop, photosKey, snapCloneIfNeeded])

  const scheduleDragPaint = useCallback(() => {
    if (rafRef.current != null) return
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      paintTrack(false)
    })
  }, [paintTrack])

  useLayoutEffect(() => {
    if (prevPhotosKeyRef.current !== photosKey) {
      prevPhotosKeyRef.current = photosKey
      internalSlideRef.current = initialInternalIndex(urls.length)
      dragPxRef.current = 0
      lastDragPxRef.current = 0
      dragStartRef.current = null
      setIsDragging(false)
      setActivePhoto(0)
      paintTrack(false)
      return
    }
    if (urls.length <= 1) return
    if (dragStartRef.current || animatingRef.current) return
    paintTrack(false)
  }, [photosKey, viewportWidth, urls.length, paintTrack])

  const imgClass = `listing-card-img${isUserPhoto ? ' listing-card-img--user' : ' listing-card-img--stock'}`

  const commitInternal = useCallback(
    (nextInternal: number, animate: boolean) => {
      dragPxRef.current = 0
      lastDragPxRef.current = 0
      const n = urls.length
      let targetInternal = clampLoopInternal(nextInternal, n)
      const cur = internalSlideRef.current
      if (cur === n + 1 && targetInternal > n + 1) {
        internalSlideRef.current = 1
        paintTrack(false)
        targetInternal = 2
      } else if (cur === 0 && targetInternal < 0) {
        internalSlideRef.current = n
        paintTrack(false)
        targetInternal = n - 1
      }
      targetInternal = clampLoopInternal(targetInternal, n)
      setActivePhoto(clampDisplayIndex(displayIndexFromInternal(targetInternal, n), n))
      if (animate) {
        animateTrackTo(targetInternal)
        internalSlideRef.current = targetInternal
      } else {
        internalSlideRef.current = targetInternal
        paintTrack(false)
        snapCloneIfNeeded()
      }
    },
    [paintTrack, animateTrackTo, urls.length, snapCloneIfNeeded],
  )

  const finishDrag = useCallback(
    (dx: number, axis: 'x' | 'y' | null) => {
      const w = viewportWidthRef.current
      const currentInternal = internalSlideRef.current
      setIsDragging(false)

      const effectiveAxis = axis ?? (Math.abs(lastDragPxRef.current) > TAP_MOVE_MAX_PX ? 'x' : null)

      if (Math.abs(dx) > TAP_MOVE_MAX_PX || Math.abs(lastDragPxRef.current) > TAP_MOVE_MAX_PX) {
        suppressClickRef.current = true
      }

      if (effectiveAxis !== 'x' || w <= 0) {
        const hadOffset = Math.abs(dx) > 0 || Math.abs(lastDragPxRef.current) > 0
        dragPxRef.current = 0
        lastDragPxRef.current = 0
        commitInternal(currentInternal, hadOffset)
        return
      }

      const threshold = Math.max(SNAP_MIN_PX, w * SNAP_RATIO)
      let nextInternal = currentInternal
      if (dx <= -threshold) nextInternal += 1
      else if (dx >= threshold) nextInternal -= 1

      if (nextInternal !== currentInternal) movedRef.current = true

      commitInternal(nextInternal, true)
    },
    [commitInternal],
  )

  const releaseDragRef = useRef(finishDrag)
  releaseDragRef.current = finishDrag

  const releasePointer = useCallback((pointerId: number, clientX: number | null) => {
    const start = dragStartRef.current
    if (!start || start.pointerId !== pointerId) return false

    const dx = clientX != null ? clientX - start.x : lastDragPxRef.current
    const axis = start.axis
    dragStartRef.current = null

    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }

    releaseDragRef.current(dx, axis)
    return true
  }, [])

  useEffect(() => {
    const onGlobalPointerEnd = (ev: Event) => {
      const e = ev as globalThis.PointerEvent
      const viewport = viewportRef.current
      if (!viewport) return
      if (!releasePointer(e.pointerId, e.clientX)) return
      try {
        if (viewport.hasPointerCapture(e.pointerId)) {
          viewport.releasePointerCapture(e.pointerId)
        }
      } catch {
        /* already released */
      }
    }

    window.addEventListener('pointerup', onGlobalPointerEnd, true)
    window.addEventListener('pointercancel', onGlobalPointerEnd, true)
    return () => {
      window.removeEventListener('pointerup', onGlobalPointerEnd, true)
      window.removeEventListener('pointercancel', onGlobalPointerEnd, true)
    }
  }, [releasePointer])

  const cancelDrag = useCallback(
    (el: HTMLDivElement, pointerId: number) => {
      releasePointer(pointerId, null)
      try {
        if (el.hasPointerCapture(pointerId)) el.releasePointerCapture(pointerId)
      } catch {
        /* already released */
      }
    },
    [releasePointer],
  )

  function onViewportClick(e: ReactMouseEvent<HTMLDivElement>) {
    if (!onTap) return
    if (suppressClickRef.current) {
      suppressClickRef.current = false
      e.preventDefault()
      e.stopPropagation()
      return
    }
    onTap()
  }

  function onWheel(e: ReactWheelEvent<HTMLDivElement>) {
    if (urls.length <= 1) return
    const dx = e.deltaX
    const dy = e.deltaY
    if (Math.abs(dx) < 2 && Math.abs(dy) < 2) return
    if (Math.abs(dx) <= Math.abs(dy) * 1.15 && !e.shiftKey) return

    e.preventDefault()
    e.stopPropagation()
    suppressClickRef.current = true

    wheelAccumRef.current += e.shiftKey ? dy : Math.abs(dx) >= Math.abs(dy) ? dx : dy
    if (Math.abs(wheelAccumRef.current) < WHEEL_STEP_PX) return

    const dir = wheelAccumRef.current > 0 ? 1 : -1
    wheelAccumRef.current = 0
    commitInternal(internalSlideRef.current + dir, true)
  }

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    onPrefetch?.()
    movedRef.current = false
    suppressClickRef.current = false
    if (urls.length <= 1) return
    e.stopPropagation()
    dragStartRef.current = { x: e.clientX, y: e.clientY, pointerId: e.pointerId, axis: null }
    dragPxRef.current = 0
    lastDragPxRef.current = 0
    setIsDragging(true)
    paintTrack(false)
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    const start = dragStartRef.current
    if (!start || start.pointerId !== e.pointerId) return

    const dx = e.clientX - start.x
    const dy = e.clientY - start.y

    if (!start.axis) {
      if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return
      const axis: 'x' | 'y' = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
      dragStartRef.current = { ...start, axis }
      if (axis === 'y') {
        cancelDrag(e.currentTarget, e.pointerId)
        return
      }
    }

    if (dragStartRef.current?.axis === 'y') return

    e.preventDefault()
    e.stopPropagation()

    let dragPx = dx
    if (Math.abs(dragPx) > TAP_MOVE_MAX_PX) movedRef.current = true

    if (!loop) {
      const display = displayIndexFromInternal(internalSlideRef.current, urls.length)
      if (display === 0 && dragPx > 0) dragPx *= 0.28
      else if (display === urls.length - 1 && dragPx < 0) dragPx *= 0.28
    }

    dragPxRef.current = dragPx
    lastDragPxRef.current = dragPx
    scheduleDragPaint()
  }

  function endPointer(e: ReactPointerEvent<HTMLDivElement>) {
    if (urls.length <= 1) return
    if (releasePointer(e.pointerId, e.clientX)) {
      if (dragStartRef.current === null) e.stopPropagation()
      try {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.releasePointerCapture(e.pointerId)
        }
      } catch {
        /* already released */
      }
    }
  }

  function onLostPointerCapture(e: ReactPointerEvent<HTMLDivElement>) {
    if (dragStartRef.current?.pointerId === e.pointerId) {
      releasePointer(e.pointerId, null)
    }
  }

  function onNavPointerDown(e: ReactPointerEvent<HTMLButtonElement>) {
    e.stopPropagation()
  }

  function onNavClick(e: ReactMouseEvent<HTMLButtonElement>, dir: -1 | 1) {
    e.preventDefault()
    e.stopPropagation()
    suppressClickRef.current = true
    movedRef.current = true
    onPrefetch?.()
    commitInternal(internalSlideRef.current + dir, true)
  }

  if (urls.length === 0) {
    return (
      <div className="listing-card-photo-carousel listing-card-photo-carousel--empty">
        <ListingNoPhotoPlaceholder />
      </div>
    )
  }

  if (urls.length === 1) {
    return (
      <div
        className="listing-card-photo-carousel listing-card-photo-carousel--single"
        onClick={onViewportClick}
        onPointerDown={onPointerDown}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onLostPointerCapture={onLostPointerCapture}
      >
        <img src={urls[0]} alt="" draggable={false} className={imgClass} />
        <ListingPhotoBrandOverlay />
      </div>
    )
  }

  return (
    <div className="listing-card-photo-carousel">
      <div
        ref={viewportRef}
        className={`listing-card-photo-carousel__viewport${isDragging ? ' listing-card-photo-carousel__viewport--dragging' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onLostPointerCapture={onLostPointerCapture}
        onClick={onViewportClick}
        onWheel={onWheel}
      >
        <div ref={trackRef} className="listing-card-photo-carousel__track">
          {slideUrls.map((src, idx) => (
            <div key={`${src}-loop-${idx}`} className="listing-card-photo-carousel__slide">
              <img src={src} alt="" draggable={false} className={imgClass} />
            </div>
          ))}
        </div>
      </div>
      <button
        type="button"
        className="listing-card-photo-carousel__nav listing-card-photo-carousel__nav--prev"
        aria-label={t('listing.galleryPrev')}
        onPointerDown={onNavPointerDown}
        onClick={(e) => onNavClick(e, -1)}
      >
        <svg className="listing-card-photo-carousel__nav-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M14.5 6.5 9 12l5.5 5.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <button
        type="button"
        className="listing-card-photo-carousel__nav listing-card-photo-carousel__nav--next"
        aria-label={t('listing.galleryNext')}
        onPointerDown={onNavPointerDown}
        onClick={(e) => onNavClick(e, 1)}
      >
        <svg className="listing-card-photo-carousel__nav-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M9.5 6.5 15 12l-5.5 5.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <ListingPhotoBrandOverlay />
      <div className="listing-card-photo-dots" aria-hidden="true">
        {urls.map((src, idx) => (
          <span
            key={`dot-${src}-${idx}`}
            className={`listing-card-photo-dot${idx === activePhoto ? ' listing-card-photo-dot--active' : ''}`}
          />
        ))}
      </div>
    </div>
  )
}
